import { sortBy } from 'lodash';
import { createContext, useContext, useMemo } from 'react';
import { useLocalStorage } from 'src/domain/useLocalStorage';
import { useAuth } from 'src/domain/auth/useAuth';
import { CustomizableColumnType, CustomizableTableConfigKey, CUSTOMIZED_TABLE_CONFIG } from './CustomizableTable';

interface CustomizableTableContextProviderProps<T> {
    configKey: CustomizableTableConfigKey;
    columns: CustomizableColumnType<T>[];
    children: React.ReactNode;
}

type ColumnSettings = {
    order: number;
    hidden: boolean;
    width: number;
};

type ColumnSettingsMap = Record<string, ColumnSettings>;

interface CustomizableTableContextValue<T> {
    columns: CustomizableColumnType<T>[];
    setColumnHidden: (key: string, hidden: boolean) => void;
    setColumnsOrder: (orderedKeys: string[]) => void;
    setColumnWidth: (key: string, width: number) => void;
    resetColumns: () => void;
    columnSettings: ColumnSettingsMap;
    allColumns: CustomizableColumnType<T>[];
    hiddenColumnsCount: number;
}

const STORAGE_PREFIX = 'customizableTable:columns:';

const CustomizableTableContext = createContext<CustomizableTableContextValue<any> | undefined>(undefined);

export function CustomizableTableContextProvider<T extends Record<string, any>>({
    configKey,
    columns,
    children,
}: CustomizableTableContextProviderProps<T>) {
    const auth = useAuth();
    const userId = auth?.user?.user_id!;
    const LOCAL_STORAGE_KEY = `${STORAGE_PREFIX}${userId}${configKey}`;
    const [columnSettings, setColumnSettings] = useLocalStorage<ColumnSettingsMap>(LOCAL_STORAGE_KEY, {});

    const allColumns = useMemo(() => {
        return columns.map((col, idx) => {
            const settings = columnSettings[col.key as string];
            let width = settings?.width || col.width || CUSTOMIZED_TABLE_CONFIG.minColumnWidth;

            const isUtilityColumn = CUSTOMIZED_TABLE_CONFIG.nonResizableKeys.includes(col.key as string);

            return {
                ...col,
                order: settings?.order ?? idx,
                hidden: settings?.hidden ?? !!col.hidden,
                width: width,
                ...(!isUtilityColumn
                    ? {
                          onHeaderCell: () => ({
                              width: width,

                              onResize: (newWidth: number) => {
                                  setColumnWidth(col.key as string, newWidth);
                              },
                          }),
                      }
                    : {}),
            };
        });
    }, [columns, columnSettings]);

    // Only visible columns, sorted by order (using lodash)
    const visibleColumns = useMemo(() => {
        return sortBy(
            allColumns.filter(col => !col.hidden),
            'order'
        );
    }, [allColumns]);

    const setColumnWidth = (key: string, width: number) => {
        setColumnSettings(prev => {
            return {
                ...prev,
                [key]: {
                    ...prev[key],
                    width: width,
                },
            };
        });
    };

    const setColumnHidden = (key: string, hidden: boolean) => {
        setColumnSettings(prev => {
            return {
                ...prev,
                [key]: {
                    ...prev[key],
                    hidden: !hidden,
                },
            };
        });
    };

    // Accepts an array of keys in the new order
    const setColumnsOrder = (orderedKeys: string[]) => {
        setColumnSettings(prev => {
            const newSettings: ColumnSettingsMap = { ...prev };
            orderedKeys.forEach((key, idx) => {
                newSettings[key] = {
                    ...newSettings[key],
                    order: idx,
                    // Do not touch hidden property here!
                };
            });

            // Keep other columns' order unchanged or move them to the end if not in orderedKeys
            Object.keys(prev).forEach(key => {
                if (!orderedKeys.includes(key)) {
                    newSettings[key] = {
                        ...newSettings[key],
                        order: orderedKeys.length + 1,
                    };
                }
            });

            return newSettings;
        });
    };

    const resetColumns = () => setColumnSettings({});

    const hiddenColumnsCount = useMemo(() => allColumns.filter(col => col.hidden).length, [allColumns]);

    return (
        <CustomizableTableContext.Provider
            value={{
                columns: visibleColumns,
                setColumnHidden,
                setColumnsOrder,
                setColumnWidth,
                resetColumns,
                columnSettings,
                allColumns,
                hiddenColumnsCount,
            }}
        >
            {children}
        </CustomizableTableContext.Provider>
    );
}

export function useCustomizableTableContext<T>() {
    const ctx = useContext(CustomizableTableContext) as CustomizableTableContextValue<T> | undefined;

    if (!ctx) throw new Error('useCustomizableTableContext must be used within CustomizableTableContextProvider');

    return ctx;
}
